// src/components/sections/EtTrinityProblems.jsx

import { motion } from "framer-motion";
import {
  PuzzlePieceIcon,
  ArrowPathIcon,
  BoltIcon,
} from "@heroicons/react/24/outline";

import {
    trinityProblem1,
    trinityProblem2,
    trinityProblem3
} from "@/assets/img/imageAssets"

// First Created – 08 Nov 2025
// Author – Atul Pandey
// Purpose – Visually immersive, alternating layout for Trinity performance challenges
// Based on “Think → Connect → Run” framing

// 1️⃣ Section data
const sections = [
  {
    title: "#1 - The Thinking Breakdown",
    icon: PuzzlePieceIcon,
    text: (
      <>
        Leaders are told to “think”, but how? What tools truly help us do it?
        <br /><br />

        Most methods are replaced with brainstorming, checklists, or AI-generated “smartness”
        that sounds good but lacks depth. 
        
        <br /><br />

        Without real clarity or vision,
        <span className="text-orange-400 font-semibold"> think performance remains dangerously underdeveloped</span>.

        <br /><br />

        Trinity’s <span className="text-orange-500 font-semibold">Induce</span> module nurtures human-led thought with structure, space, and purpose.
      </>
    ),
    image: trinityProblem1, // Placeholder
  },
  {
    title: "#2 - The Missing Connection",
    icon: ArrowPathIcon,
    text: (
      <>
        Most actions don’t flow from strategy. They respond to noise.
        
        <br /><br />
        Meetings, escalations, firefights. Execution gets decoupled from mission.

        <br /><br />
        Even well-designed strategies stay trapped in slides and playbooks.
        
        <br /><br />
        Trinity connects <span className="text-orange-400 font-semibold">thoughts to actions</span> through
        its <span className="text-orange-500 font-semibold">Imprint → Imagine → Action</span> backbone, creating
        <span className="text-orange-400 font-semibold"> mission-linked execution</span> from the start.
      </>
    ),
    image: trinityProblem2, // Placeholder
  },
  {
    title: "#3 - The Run Without Reflection",
    icon: BoltIcon,
    text: (
      <>
        In most orgs, <span className="text-orange-600 font-bold animate-pulse">"busyness"</span> replaces <span className="text-orange-600 font-extrabold">business</span>. 
        
        <br /><br />
        Actions happen, but no one sees,
        tracks, or learns from them. Over time, fatigue builds, and impact fades.

        <br /><br />

        Trinity’s <span className="text-orange-500 font-semibold">Illuminate</span> module surfaces daily execution,
        measures performance habits, and reflects progress. 
        
        <br /><br />
        This is where
        <span className="text-orange-400 font-semibold"> purpose becomes visible</span>.
      </>
    ),
    image: trinityProblem3, // Placeholder
  },
];

// 2️⃣ Animation variants
const fadeVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

// 3️⃣ Component
export default function EtTrinityProblems() {
  return (
    <section
      id="problems"
      className="relative py-24 bg-black text-white overflow-hidden"
    >
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-orange-900/20 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,102,0,0.2)_0%,transparent_70%)] mix-blend-screen animate-[pulse_12s_ease-in-out_infinite]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.h2
          className="text-center text-3xl md:text-4xl font-bold text-orange-500 mb-6"
          initial="hidden"
          whileInView="visible"
          variants={fadeVariants}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          {/* Why Performance Breaks — and How Trinity Repairs It */}
          {/* From Purpose to Performance : Why Most Strategies Don’t Deliver */}
          From Purpose to Performance : Making Strategy Work in Practice
        </motion.h2>

        <motion.p
          className="text-center text-gray-300 max-w-3xl mx-auto mb-20 text-lg leading-relaxed font-extralight"
          initial="hidden"
          whileInView="visible"
          variants={fadeVariants}
          transition={{ delay: 0.2, duration: 0.6 }}
          viewport={{ once: true }}
        >
          {/* Trinity doesn’t just track work — it helps you think better, act with purpose,
          and see what truly matters. Here’s how it fixes the breakdown in organizational performance. */}
          Even the best intentions fall apart without <span className="font-bold text-orange-600 animate-pulse">alignment</span>. Trinity bridges the gap between what leaders envision and what teams execute. By addressing the <span className="font-bold text-orange-600">root causes</span> behind stalled or low growth.  How organizations think, act, and reflect.
        </motion.p>

        {/* Alternating Panels */}
        <div className="space-y-32">
          {sections.map((sec, i) => {
            const Icon = sec.icon;
            const isEven = i % 2 === 0;
            return (
              <motion.div
                key={sec.title}
                className={`flex flex-col md:flex-row ${
                  isEven ? "" : "md:flex-row-reverse"
                } items-center gap-10 md:gap-20`}
                initial="hidden"
                whileInView="visible"
                variants={fadeVariants}
                transition={{ duration: 0.8, delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                {/* Text Block */}
                <div className="md:w-1/2 space-y-5">
                  <div className="flex items-center gap-3">
                    <Icon className="h-8 w-8 text-orange-500" />
                    <h3 className="text-2xl md:text-3xl font-semibold text-white">
                      {sec.title}
                    </h3>
                  </div>
                  <p className="text-gray-200 text-base md:text-lg leading-relaxed font-extralight">
                    {sec.text}
                  </p>
                </div>

                {/* Image Block */}
                <motion.div
                  className="relative md:w-1/2 rounded-2xl overflow-hidden shadow-2xl"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <motion.div
                    className="absolute inset-0 bg-orange-600/10 blur-2xl rounded-2xl"
                    animate={{ opacity: [0.15, 0.35, 0.15] }}
                    transition={{
                      duration: 8,
                      ease: "easeInOut",
                      repeat: Infinity,
                    }}
                  />
                  <motion.img
                    src={sec.image}
                    alt={sec.title}
                    className="relative w-full h-96 object-cover border border-orange-900/20 rounded-2xl"
                    animate={{ y: [0, -10, 0] }}
                    transition={{
                      duration: 10,
                      ease: "easeInOut",
                      repeat: Infinity,
                    }}
                    whileHover={{ scale: 1.05 }}
                  />
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
